<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { insightsApi } from '@/api/client'
import { Skeleton } from '@/components/ui/skeleton'
import { sideConfig } from '@/lib/product'
import InsightsKpiTiles from '@/components/InsightsKpiTiles.vue'
import InsightsTrendChart from '@/components/InsightsTrendChart.vue'
import InsightsTopDevices from '@/components/InsightsTopDevices.vue'
import InsightsSegmented from '@/components/InsightsSegmented.vue'
import InsightsInfoTip from '@/components/InsightsInfoTip.vue'
import {
    INSIGHTS_RANGES,
    INSIGHTS_SIDES,
    currencySymbol,
    scopeQuery,
    sideConfigKey,
    type InsightsChart,
    type InsightsRange,
    type InsightsScope,
    type InsightsSummary,
    type InsightsTopDevices as TopDevices,
} from '@/lib/insights'

/**
 * By model: the headline metrics, the price and listings chart and the top devices,
 * all for one scope. The chart reloads on its own when only the range changes.
 */
const props = defineProps<{
    scope: InsightsScope
    /** Changes exactly when the scope does; the scope object itself is rebuilt on every pass. */
    scopeKey: string
    range: InsightsRange
    /** Loaded by the page, which also shows its context line; null while loading. */
    summary: InsightsSummary | null
}>()

const emit = defineEmits<{
    'update:range': [range: InsightsRange]
}>()

const { t } = useI18n()

const topDevices = ref<TopDevices | null>(null)
const chart = ref<InsightsChart | null>(null)
const failed = ref(false)

let scopeRequest = 0
let chartRequest = 0

const loadScope = async () => {
    const token = ++scopeRequest
    const query = scopeQuery(props.scope)

    failed.value = false

    try {
        const { data } = await insightsApi.topDevices(query)

        if (token === scopeRequest) {
            topDevices.value = data
        }
    } catch {
        if (token === scopeRequest) {
            failed.value = true
        }
    }
}

const loadChart = async () => {
    const token = ++chartRequest

    try {
        const { data } = await insightsApi.chart(scopeQuery(props.scope, { range: props.range }))

        if (token === chartRequest) {
            chart.value = data
        }
    } catch {
        if (token === chartRequest) {
            failed.value = true
        }
    }
}

watch(() => props.scopeKey, loadScope, { immediate: true })
watch(() => [props.scopeKey, props.range], loadChart, { immediate: true })

/** The legend names the sides the chart draws; the chart's own legend was one entry per series. */
const chartSides = computed(() =>
    INSIGHTS_SIDES.filter((side) => chart.value?.[side] != null).map((side) => ({
        side,
        label: sideConfig[sideConfigKey(side)].label,
    })),
)

const symbol = computed(() => currencySymbol(props.summary?.currency))

const rangeOptions = computed(() =>
    INSIGHTS_RANGES.map((range) => ({ value: range, label: t(`insights.chart.ranges.${range}`) })),
)
</script>

<template>
    <p v-if="failed" class="text-sm text-destructive">{{ t('common.errors.somethingWrong') }}</p>

    <div v-else class="space-y-8">
        <InsightsKpiTiles v-if="summary" :summary="summary" :currency-symbol="symbol" />
        <div v-else class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Skeleton v-for="index in 5" :key="index" class="h-36" />
        </div>

        <div class="rounded-xl border bg-card p-6">
            <div class="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div class="space-y-1">
                    <h2 class="flex items-center gap-1.5 font-semibold">
                        {{ t('insights.chart.title') }}
                        <InsightsInfoTip :title="t('insights.chart.listings')" :text="t('insights.tips.listings')" />
                    </h2>
                    <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <span v-for="item in chartSides" :key="item.side" class="flex items-center gap-1.5">
                            <span
                                class="size-2 rounded-full"
                                :class="item.side === 'resell' ? 'bg-resell' : 'bg-tradein'"
                            />
                            {{ item.label }}
                        </span>
                        <span>{{ t('insights.chart.subtitle') }}</span>
                    </div>
                </div>
                <InsightsSegmented
                    :model-value="range"
                    :options="rangeOptions"
                    @update:model-value="emit('update:range', $event)"
                />
            </div>
            <InsightsTrendChart v-if="chart" :chart="chart" :currency-symbol="currencySymbol(chart.currency)" />
            <Skeleton v-else class="h-96 w-full" />
        </div>

        <InsightsTopDevices v-if="topDevices" :data="topDevices" :currency-symbol="symbol" />
        <Skeleton v-else class="h-64 w-full" />
    </div>
</template>
